import { isAxiosError } from 'axios';
import { StateCreator } from 'zustand';
import { useOAuth } from '../oauth';
import { IUserSlice, TUsersStore } from './types';
import { authApi, userApi } from '@/API';
import { useInstitutionsStore } from '@/stores/institutions';
import { EServerError, TUserDataCamel } from '@/types';
import {
  EUserRoles,
  TUserRoleInstitutionSettingsCamelByRole,
} from '@/types/user';
import { convertKeys } from '@/utils/common';
import { LOCAL_STORAGE_KEYS } from '@/utils/constants';
import { sortByUserInstitutionRoles } from '@/utils/users';
import { useSurveyStore } from '@/stores/survey';
import { useWhitelabelStore } from '../whitelabel';
import { useAppointmentsStore } from '../appointments';

export const userSlice: StateCreator<TUsersStore, [], [], IUserSlice> = (
  set,
  get,
) => ({
  userData: null,
  allRoles: [],
  loading: false,
  role: null,
  roleId: null,
  userId: null,
  deviceId: null,
  availableInstitutionIds: [],

  getRole: () => get().role,
  getAllRoles: () => get().allRoles,
  getRoleId: () => get().roleId,
  getUserData: () => get().userData,

  getUserInstitutionRoleByRole: <R extends EUserRoles>(userRole: R) => {
    const { currentInstitution } = useInstitutionsStore.getState();
    const userInstitutionRoles = get().userData?.userInstitutionRoles;

    const result = userInstitutionRoles?.find(
      (item): item is TUserRoleInstitutionSettingsCamelByRole<R> =>
        item.role === userRole && item.institutionId === currentInstitution?.id,
    );

    return result ?? null;
  },

  getUserInstitutionRoleByParticipantIds: (participantIds) => {
    const userInstitutionRoles = get().userData?.userInstitutionRoles;
    const filterUsersInstitutionRolesByParticipantIds =
      userInstitutionRoles?.filter(({ id }) => participantIds.includes(id)) ??
      [];
    return sortByUserInstitutionRoles(
      filterUsersInstitutionRolesByParticipantIds,
    )[0];
  },

  getAllUserInstitutionRolesByParticipantIds: (participantIds) => {
    const userInstitutionRoles = get().userData?.userInstitutionRoles;
    const filterUsersInstitutionRolesByParticipantIds =
      userInstitutionRoles?.filter(({ id }) => participantIds.includes(id)) ??
      [];
    return sortByUserInstitutionRoles(
      filterUsersInstitutionRolesByParticipantIds,
    );
  },

  getUserInstitutionRoleByInstitutionId: (institutionId) => {
    const userInstitutionRoles = get().userData?.userInstitutionRoles;
    const filterUsersInstitutionRolesByParticipantIds =
      userInstitutionRoles?.filter(
        (userInstitutionRole) =>
          userInstitutionRole.institutionId === institutionId,
      ) ?? [];
    return sortByUserInstitutionRoles(
      filterUsersInstitutionRolesByParticipantIds,
    )[0];
  },

  setAndGetUserInstitution: async (userData: TUserDataCamel, id?: string) => {
    const { userInstitutionRoles } = userData;
    const {
      getCurrentInstitution,
      getInstitutions,
      setCurrentInstitutionById,
    } = useInstitutionsStore.getState();
    if (!getCurrentInstitution()) {
      await getInstitutions();
    }
    let institutionId = getCurrentInstitution()?.id;
    if (institutionId) {
      return institutionId;
    }
    if (id) {
      setCurrentInstitutionById(id);
      return id;
    }
    const { institutions } = useInstitutionsStore.getState();
    const currentSubdomain = window.location.host.split('.')[0]?.toLowerCase();
    // trying to set current institution according to subdomain
    const subdomainInstitution = institutions.find(
      (item) =>
        item?.whitelabel?.subdomain?.toLowerCase() === currentSubdomain &&
        item?.whitelabel?.enabled,
    );
    // fallback - first role
    institutionId =
      subdomainInstitution?.id ?? userInstitutionRoles?.[0]?.institutionId;
    setCurrentInstitutionById(institutionId);
    return institutionId;
  },

  getRolesDataFromUser: (userData: TUserDataCamel, institutionId: string) => {
    const { userInstitutionRoles, isSuperAdmin } = userData;
    // collecting all roles for user under this institution
    const allRolesWithIds = userInstitutionRoles.filter(
      (value) => value.institutionId === institutionId,
    );
    if (allRolesWithIds.length === 0 && !userData.isSuperAdmin) {
      console.error(
        'Fetching user data failed:',
        'You do not have roles in this institution',
      );
      return;
    }

    const sortedRoles = sortByUserInstitutionRoles(allRolesWithIds);

    const priorityRole = sortedRoles[0];

    if (!priorityRole && !isSuperAdmin) {
      console.error('Fetching user data failed:', 'Unknown role');
      return;
    }

    const isSuperAdminWithoutRole = !priorityRole?.role && isSuperAdmin;

    const role = isSuperAdminWithoutRole
      ? EUserRoles.ADMIN
      : priorityRole?.role;

    const deviceId =
      priorityRole?.role === EUserRoles.DEVICE
        ? priorityRole.meta?.deviceId
        : null;

    const availableInstitutionIds: string[] = Array.from(
      new Set(userInstitutionRoles.map(({ institutionId }) => institutionId)),
    );

    const allRoles = allRolesWithIds.map((item) => item.role);
    if (isSuperAdmin) allRoles.push(EUserRoles.ADMIN);

    return {
      role,
      roleId: priorityRole?.id,
      deviceId,
      allRoles,
      availableInstitutionIds,
    };
  },

  fetchUserData: async (id) => {
    set({ loading: true });
    try {
      const userResponse = await userApi.getMe();
      const userData = convertKeys(userResponse.data.data);
      // INIT OAuth calendars
      useOAuth
        .getState()
        .setOAuthConnections(userData.meta?.oauthConnections ?? []);
      const { userInstitutionRoles } = userData;
      if (userInstitutionRoles.length === 0) {
        console.error('Fetching user data failed:', 'Empty array of roles');
        return;
      }
      const institutionId = await get().setAndGetUserInstitution(userData, id);

      const values = get().getRolesDataFromUser(userData, institutionId);
      if (!values) {
        console.error(`No userInstitutionRole for selected institution ${id}`);
        return;
      }
      const { role, roleId, deviceId, allRoles, availableInstitutionIds } =
        values;
      set({
        userData,
        userId: userData.id,
        role,
        roleId,
        deviceId,
        allRoles,
        availableInstitutionIds,
      });
      return userData;
    } catch (error) {
      await get().logout();
      console.error('Fetching user data failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  checkRole: (role: EUserRoles) => {
    return get().allRoles.includes(role);
  },

  checkRoleIds: (roleIds) => {
    return !!get().userData?.userInstitutionRoles.some(({ id }) =>
      roleIds.includes(id),
    );
  },

  hasAnyRoles: (roleOrRoles: EUserRoles | EUserRoles[]) => {
    const userRoles = get().allRoles;
    const rolesToCheck = Array.isArray(roleOrRoles)
      ? roleOrRoles
      : [roleOrRoles];
    return rolesToCheck.some((role) => userRoles.includes(role));
  },

  setUserData: (data) => {
    set({
      userData: data,
    });
  },

  setAvailability: (availaibity) => {
    set((state) =>
      state.userData
        ? {
            userData: {
              ...state.userData,
              availability: availaibity,
              meta: {
                ...state.userData?.meta,
                isAvailable: availaibity.isAvailable,
              },
            },
          }
        : { userData: null },
    );
  },

  saveUserInfo: async (
    data: Partial<TUserDataCamel> & { deviceId?: string },
    id?: string,
  ) => {
    set({ loading: true });
    const userId = id ?? get().userId;
    if (!userId) {
      throw new Error('UserId is missing');
    }
    try {
      const response = await userApi.updateUserInfo(
        userId,
        convertKeys(data, 'snake'),
      );
      const updatedUser = convertKeys(response.data.data);
      if (!id || get().userId === id) {
        if (data.deviceId) {
          set({ deviceId: data.deviceId });
        }
        set((state) => ({
          userData: {
            ...updatedUser,
            availability:
              state.userData?.availability ?? updatedUser.availability,
          },
        }));
      }
      get().updateUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('Saving user info failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  uploadInsuranceCardPhoto: async (data) => {
    set({ loading: true });
    try {
      const userId = get().userId ?? '';
      const response = await userApi.uploadInsuranceCardPhoto(data);
      const responseUpdate = await userApi.updateUserInfo(userId, {
        meta: {
          insurance_card_photo: response.data.data,
        },
      });
      const user = convertKeys(responseUpdate.data.data);
      get().updateUser(user);
      set({ userData: user });
    } catch (error) {
      console.error('Uploading profile picture failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  uploadPhotoId: async (data) => {
    set({ loading: true });
    try {
      const userId = get().userId ?? '';
      const response = await userApi.uploadPhoneId(data);
      const responseUpdate = await userApi.updateUserInfo(userId, {
        meta: {
          photo_id: response.data.data,
        },
      });
      const user = convertKeys(responseUpdate.data.data);

      get().updateUser(user);
      set({ userData: user });
    } catch (error) {
      console.error('Uploading profile picture failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  uploadProfilePicture: async (data) => {
    set({ loading: true });
    try {
      const userId = get().userId ?? '';
      const response = await userApi.uploadProfilePicture(data);
      const responseUpdate = await userApi.updateUserInfo(userId, {
        meta: {
          profile_picture: response.data.data,
        },
      });
      get().updateUser(convertKeys(responseUpdate.data.data));
      set(({ userData }) => ({
        userData: userData
          ? {
              ...userData,
              meta: {
                ...userData.meta,
                profilePicture: response.data.data,
              },
            }
          : null,
      }));
    } catch (error) {
      console.error('Uploading profile picture failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  deleteProfilePicture: async () => {
    set({ loading: true });
    try {
      const userId = get().userId ?? '';
      await userApi.updateUserInfo(userId, {
        meta: {
          profile_picture: '',
        },
      });
      set(({ userData }) => ({
        userData: userData
          ? {
              ...userData,
              meta: {
                ...userData.meta,
                profilePicture: '',
              },
            }
          : null,
      }));
    } catch (error) {
      console.error('Deleting profile picture failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  changeApplicationLanguage: async (language) => {
    set({ loading: true });
    try {
      const userId = get().userId ?? '';
      await userApi.updateUserInfo(userId, { language });
      set(({ userData }) => ({
        userData: userData
          ? {
              ...userData,
              language,
            }
          : null,
      }));
    } catch (error) {
      console.error('Changing application language failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  changeTimeZone: async (timezone: string) => {
    set({ loading: true });
    try {
      const userId = get().userId ?? '';
      await userApi.updateUserInfo(userId, { timezone });
      set(({ userData }) => ({
        userData: userData
          ? {
              ...userData,
              timezone,
            }
          : null,
      }));
    } catch (error) {
      console.error('Changing timezone failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  updateLicenseToPractice: async (licenseLocations) => {
    set({ loading: true });
    try {
      const userId = get().userId ?? '';
      await userApi.updateUserInfo(userId, {
        license_locations: licenseLocations,
      });
      set(({ userData }) => ({
        userData: userData
          ? {
              ...userData,
              licenseLocations: licenseLocations,
            }
          : null,
      }));
    } catch (error) {
      console.error('Updating license to practice failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  deleteAccount: async () => {
    set({ loading: true });
    try {
      await userApi.deleteMe();
      set({
        userData: null,
        userId: null,
        role: null,
      });
    } catch (error) {
      console.error('Deleting user failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  updateDosespot: async (body) => {
    try {
      const response = await userApi.updateDosespot(convertKeys(body, 'snake'));
      const user = convertKeys(response.data.data);
      set({ userData: user });
      return user;
    } catch (error) {
      console.error(error);
      if (isAxiosError(error)) {
        const errorWithDetails = new Error(
          error.response?.data.error || EServerError.UNKNOWN_ERROR,
        );
        errorWithDetails.cause = error.response?.data?.meta;
        throw errorWithDetails;
      }
      throw new Error(EServerError.UNKNOWN_ERROR);
    }
  },

  postDosespotSso: async (body) => {
    try {
      const response = await userApi.postDosespotSso(
        convertKeys(body, 'snake'),
      );
      return response.data.data;
    } catch (error) {
      console.error(error);
      if (isAxiosError(error)) {
        const errorWithDetails = new Error(
          error.response?.data.error || EServerError.UNKNOWN_ERROR,
        );
        errorWithDetails.cause = error.response?.data?.meta;
        throw errorWithDetails;
      }
    }
  },

  clearUserState: () => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.PAYMENT_REDIRECT);
    set({
      userData: null,
      userId: null,
      role: null,
      roleId: null,
      allRoles: [],
      availableInstitutionIds: [],
      manualNotes: {},
    });
  },

  checkIsSuperAdmin: () => !!get().userData?.isSuperAdmin,

  clearStates: () => {
    get().clearUserState();
    useSurveyStore.getState().clearInfermedicaState();
    useInstitutionsStore.getState().clearInstitutionsStore();
    useWhitelabelStore.getState().clearWhitelabelStore();
    useAppointmentsStore.getState().clearAppointmentsStore();
  },

  logout: async () => {
    set({ loading: true });
    try {
      await authApi.logout();
    } catch {
      console.error('Logout failed');
    } finally {
      get().clearStates();
      set({ loading: false });
    }
  },
});
